import {
	BrowserWindow,
	BrowserWindowSymbol,
	EditorFeatureConfigSymbol,
	isViewerFragmentSymbol,
	ViewerManagerStructureAPISymbol,
} from '@wix/thunderbolt-symbols'
import { SiteMeasureMap, ViewerAPI } from 'thunderbolt-viewer-manager-types'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type {
	MeasureMapApi as MeasureMapApiType,
	MeasureMapApiHandlers,
	SiteIdentifiersMeasures,
	SiteMeasures,
	ViewerManagerLayoutApiEditorConfig,
} from '../types'
import { name } from '../symbols'
import { getInflatedAncestors } from '../utils'
import { getFullId, isDisplayedOnly } from '@wix/thunderbolt-commons'
import { ComponentsMeasuresAPISymbol, IComponentsMeasuresAPI } from 'ds-feature-components-measures'
import { MeasuresApiSymbol } from 'ds-feature-measures-api'
import type { CustomMeasureType, IMeasuresApi } from '@wix/thunderbolt-becky-types'
import { throttle } from 'lodash'
import { IModelUpdatesHandlersImplementor } from 'ds-feature-model-updates-invoker'

const SITE_IDENTIFIERS: Array<string> = ['device', 'screen', 'site']

const STRUCTURE_WIDTH: Record<string, string> = {
	SITE_HEADER_WRAPPER: 'SITE_HEADER',
	SITE_FOOTER_WRAPPER: 'SITE_FOOTER',
	PAGES_CONTAINER: 'PAGES_CONTAINER',
	SITE_PAGES: 'SITE_PAGES',
}

const WRAPPERS_COMPONENTS_MAP: Record<string, string> = {
	SITE_HEADER: 'SITE_HEADER_WRAPPER',
	SITE_FOOTER: 'SITE_FOOTER_WRAPPER',
}

const resetCache = () => ({
	left: {},
	top: {},
	width: {},
	height: {},
})

let useCache = false

type MeasureMapCache = {
	width: SiteMeasureMap['width']
	height: SiteMeasureMap['height']
	top: SiteMeasureMap['top']
	left: SiteMeasureMap['left']
}
let cache: MeasureMapCache = resetCache()

const withCache = (measureType: keyof MeasureMapCache, fn: (id: string) => number) => {
	return (compId: string) => {
		if (useCache && cache[measureType][compId] !== undefined && isDisplayedOnly(compId)) {
			return cache[measureType][compId]
		}

		const val = fn(compId)
		cache[measureType][compId] = val
		return val
	}
}
const measureMapApiFactory = (
	config: ViewerManagerLayoutApiEditorConfig,
	window: NonNullable<BrowserWindow>,
	structureViewerApi: ViewerAPI['structure'],
	measuresApi: IMeasuresApi,
	componentsMeasuresAPI: IComponentsMeasuresAPI,
	isViewerFragment: boolean
): MeasureMapApiType & IModelUpdatesHandlersImplementor<MeasureMapApiHandlers> => {
	const { isValidMeasure } = componentsMeasuresAPI

	const createProxy = <
		T extends keyof SiteMeasureMap,
		K extends Record<string, any> = SiteMeasureMap[T] extends object ? SiteMeasureMap[T] : never
	>(
		get: (identifier: string) => K[string]
	) =>
		new Proxy<K>({} as K, {
			get: (__, identifier: string) => get(WRAPPERS_COMPONENTS_MAP[identifier] || identifier),
		})

	const measureSite = (): SiteMeasures => {
		const clientWidth = window.document.body.clientWidth
		const clientHeight = window.document.documentElement.clientHeight

		return {
			clientWidth,
			clientHeight,
			siteMarginBottom: 0,
			screen: {
				width: config.isMobileView ? 320 : clientWidth,
				height: clientHeight,
				innerWidth: window.innerWidth,
				innerHeight: window.innerHeight,
			},
			device: {
				width: window.screen.width,
				height: window.screen.height,
			},
			site: {
				width: config.siteWidth,
				left: config.isMobileView ? 0 : Math.max((clientWidth - config.siteWidth) / 2, 0),
			},
		}
	}
	let siteMeasures = measureSite()

	if (!isViewerFragment) {
		const resizeObserver = new ResizeObserver(
			throttle(() => {
				siteMeasures = measureSite()
			}, 100)
		)
		resizeObserver.observe(window.document.body)
	}

	const parseFloatComputedStyleValue = (value: string) => {
		const floatValue = parseFloat(value as string)
		if (!isNaN(floatValue)) {
			return floatValue
		}
		return 0
	}

	const getMeasure = (compId: string, key: keyof CSSStyleDeclaration) => {
		const element = measuresApi.getCompElement(compId)

		if (element) {
			const value = window.getComputedStyle(element)[key] || ''
			return parseFloatComputedStyleValue(value as string)
		}
		return 0
	}

	const calculateAbsolutePosition = (compId: string, position: 'top' | 'left') => {
		const element = measuresApi.getCompElement(compId)
		if (element) {
			const getCompMeasure = (id: string, elem: HTMLElement) =>
				position === 'top' ? measureMap.top[id] : elem.offsetLeft

			const isCompFixed = structureViewerApi.getStructure(getFullId(compId)).layout.fixedPosition
			let compMeasure = getCompMeasure(compId, element)

			if (!isCompFixed) {
				const compAncestors = getInflatedAncestors(compId, config.compIdToAncestors, structureViewerApi)
				compAncestors.every((ancestorId) => {
					const ancestor = measuresApi.getCompElement(ancestorId)
					compMeasure += ancestor ? getCompMeasure(ancestorId, ancestor) : 0

					const isAncestorFixed = structureViewerApi.getStructure(getFullId(ancestorId))?.layout.fixedPosition
					return !(isAncestorFixed || ancestorId === 'masterPage') // break loop (return false) if ancestor is fixed or is masterPage (root)
				})
			}
			return compMeasure
		}

		return 0
	}

	const tryUsingCustomMeasure = (measure: CustomMeasureType, id: string) =>
		componentsMeasuresAPI.tryUsingCustomMeasure(id, measure, measureMap, measuresApi)

	const measureMap: SiteMeasureMap = {
		absoluteLeft: createProxy<'absoluteLeft'>((compId) => calculateAbsolutePosition(compId, 'left')),
		absoluteTop: createProxy<'absoluteTop'>((compId) => calculateAbsolutePosition(compId, 'top')),
		minHeight: createProxy<'minHeight'>((compId) => {
			const customMeasure = tryUsingCustomMeasure('minHeight', compId)
			if (isValidMeasure(customMeasure)) {
				return customMeasure
			}
			return getMeasure(compId, 'minHeight')
		}),
		minWidth: createProxy<'minWidth'>((compId) => {
			const customMeasure = tryUsingCustomMeasure('minWidth', compId)
			if (isValidMeasure(customMeasure)) {
				return customMeasure
			}
			return getMeasure(compId, 'minWidth')
		}),
		innerHeight: createProxy<'innerHeight'>((identifier) => {
			if (SITE_IDENTIFIERS.includes(identifier)) {
				const measures = siteMeasures[identifier as keyof SiteMeasures] as Partial<SiteIdentifiersMeasures>
				return measures.innerHeight || 0
			}
			return 0
		}),
		innerWidth: createProxy<'innerWidth'>((identifier) => {
			if (SITE_IDENTIFIERS.includes(identifier)) {
				const measures = siteMeasures[identifier as keyof SiteMeasures] as Partial<SiteIdentifiersMeasures>
				return measures.innerWidth || 0
			}
			return 0
		}),
		left: createProxy<'left'>(
			withCache('left', (identifier) => {
				const customMeasure = tryUsingCustomMeasure('left', identifier)
				if (isValidMeasure(customMeasure)) {
					return customMeasure
				}

				if (SITE_IDENTIFIERS.includes(identifier)) {
					const measures = siteMeasures[identifier as keyof SiteMeasures] as Partial<SiteIdentifiersMeasures>
					return measures.left || 0
				}
				const offsetLeftMeasure = tryUsingCustomMeasure('leftOffset', identifier)
				if (isValidMeasure(offsetLeftMeasure)) {
					const element = measuresApi.getCompElement(identifier)
					return element ? measuresApi.getCompLeft(element, identifier, measureMap) + offsetLeftMeasure : 0
				}
				const element = measuresApi.getCompElement(identifier)
				return element ? measuresApi.getCompLeft(element, identifier, measureMap) : 0
			})
		),
		height: createProxy<'height'>(
			withCache('height', (identifier) => {
				const customMeasure = tryUsingCustomMeasure('height', identifier)
				if (isValidMeasure(customMeasure)) {
					return customMeasure
				}
				if (SITE_IDENTIFIERS.includes(identifier)) {
					const measures = siteMeasures[identifier as keyof SiteMeasures] as Partial<SiteIdentifiersMeasures>
					return measures.height || 0
				}

				const element = measuresApi.getCompElement(identifier)
				return element ? measuresApi.getCompHeight(element, identifier) : 0
			})
		),
		top: createProxy<'top'>(
			withCache('top', (compId) => {
				const customMeasure = tryUsingCustomMeasure('top', compId)
				if (isValidMeasure(customMeasure)) {
					return customMeasure
				}
				const offsetTopMeasure = tryUsingCustomMeasure('topOffset', compId)
				if (isValidMeasure(offsetTopMeasure)) {
					const element = measuresApi.getCompElement(compId)
					return element ? measuresApi.getCompTop(element, compId, measureMap) + offsetTopMeasure : 0
				}
				const element = measuresApi.getCompElement(compId)
				return element ? measuresApi.getCompTop(element, compId, measureMap) : 0
			})
		),
		width: createProxy<'width'>(
			withCache('width', (identifier) => {
				const customMeasure = tryUsingCustomMeasure('width', identifier)
				if (isValidMeasure(customMeasure)) {
					return customMeasure
				}

				if (SITE_IDENTIFIERS.includes(identifier)) {
					const measures = siteMeasures[identifier as keyof SiteMeasures] as Partial<SiteIdentifiersMeasures>
					return measures.width || 0
				}

				const structureWidth = STRUCTURE_WIDTH[identifier]
				if (structureWidth) {
					const compStructure = structureViewerApi.getStructure(structureWidth)
					return compStructure?.layout.width || 980
				}

				const element = measuresApi.getCompElement(identifier)
				return element ? measuresApi.getCompWidth(element, identifier) : 0
			})
		),
		contentHeight: createProxy<'contentHeight'>((identifier) => {
			const customMeasure = tryUsingCustomMeasure('contentHeight', identifier)
			if (isValidMeasure(customMeasure)) {
				return customMeasure
			}

			return 0
		}),
		zIndex: createProxy<'zIndex'>((compId) => {
			const pinnedLayerId = `${compId}-pinned-layer`
			const compAncestors = config.compIdToAncestors[compId] || { allAncestors: [] }
			const parentId = compAncestors.allAncestors[0]

			if (parentId === pinnedLayerId) {
				return getMeasure(pinnedLayerId, 'zIndex')
			}
			return getMeasure(compId, 'zIndex')
		}),
		// @ts-ignore TODO: add to interface
		relativeToScreenOverrides: createProxy<'relativeToScreenOverrides'>((compId) => {
			const customMeasure = tryUsingCustomMeasure('relativeToScreenOverrides', compId)
			if (isValidMeasure(customMeasure)) {
				return customMeasure
			}

			const element = measuresApi.getCompElement(compId)
			const compStructure = structureViewerApi.getStructure(compId)
			if (element && compStructure) {
				const isFixedInMobile = config.isMobileView && compStructure.layout.fixedPosition

				const docked = compStructure.layout.docked
				const isHorizontallyStretched = docked && docked.right && docked.left

				if (isHorizontallyStretched && isFixedInMobile) {
					return {
						x: element.getBoundingClientRect().left,
					}
				}
			}
		}),
		get clientHeight() {
			return siteMeasures.clientHeight
		},
		get clientWidth() {
			return siteMeasures.clientWidth
		},
		get siteMarginBottom() {
			return siteMeasures.siteMarginBottom
		},
		pageBottomByComponents: createProxy<'pageBottomByComponents'>(() => 0),
		custom: createProxy<'custom'>((identifier) => {
			const customMeasure = tryUsingCustomMeasure('custom', identifier)
			if (isValidMeasure(customMeasure)) {
				return customMeasure
			}
		}),
	}

	return {
		clearCache: () => {
			cache = resetCache()
		},
		getBasicMeasureForComp: (compId: string) => {
			const element = measuresApi.getCompElement(compId)
			if (!element) {
				return {} as any
			}
			useCache = true

			const x = measureMap.left[compId]
			const y = measureMap.top[compId]
			const width = measureMap.width[compId]
			const height = measureMap.height[compId]

			useCache = false

			return { x, y, width, height }
		},
		getSiteMeasureMap: () => measureMap,
		isShowOnFixedPosition: (compId: string): boolean => {
			const element = measuresApi.getCompElement(compId)
			if (!element) {
				return false
			}

			return measuresApi.isCompFixed(element)
		},
		featureName: name,
		handlers: {
			updateSiteMeasures: () => {
				siteMeasures = measureSite()
			},
		},
	}
}

export const MeasureMapApi = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		ViewerManagerStructureAPISymbol,
		MeasuresApiSymbol,
		ComponentsMeasuresAPISymbol,
		isViewerFragmentSymbol,
	],
	measureMapApiFactory
)
